import { UserIcon, UsersIcon } from "@heroicons/react/24/outline";
import { cn } from "@utils";

const AVATAR_COLORS = [
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-pink-500",
];

function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

// Deterministic colour so a given person keeps the same avatar tint.
function colorFor(key = "") {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// Grey circular avatar used for account rows / the Account pill.
function AccountAvatar({ className }) {
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-full bg-zinc-200 text-zinc-500",
        className,
      )}
    >
      <UsersIcon aria-hidden="true" className="size-3.5" />
    </span>
  );
}

function FilterPill({ active, onSelect, icon, label }) {
  return (
    <button
      type="button"
      // Keep the editor's selection intact when toggling a filter.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      className={cn(
        "inline-flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 text-sm font-medium ring-1 ring-inset transition-colors",
        active
          ? "bg-primary-50 text-primary-600 ring-primary-200"
          : "text-zinc-700 ring-zinc-300 hover:bg-zinc-50",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// Floating panel shown while the user is typing an @-mention. Renders the
// Account / Staff filter pills and the merged customer + staff results.
// Selection is driven by the parent editor.
export default function MentionPopover({
  results,
  isFetching,
  activeFilter,
  onFilterChange,
  activeIndex,
  onHoverIndex,
  onSelect,
  showFilters = true,
  placement = "below",
}) {
  return (
    <div
      className={cn(
        "absolute inset-x-0 z-50 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-lg",
        placement === "above" ? "bottom-full mb-2" : "top-full mt-1",
      )}
    >
      {showFilters && (
        <div className="flex items-center gap-2 p-3">
          <FilterPill
            active={activeFilter === "customer"}
            onSelect={() => onFilterChange("customer")}
            icon={<AccountAvatar className="size-6" />}
            label="Account"
          />
          <FilterPill
            active={activeFilter === "staff"}
            onSelect={() => onFilterChange("staff")}
            icon={
              <span className="flex size-6 items-center justify-center rounded-full bg-primary text-white">
                <UserIcon aria-hidden="true" className="size-3.5" />
              </span>
            }
            label="Staff"
          />
        </div>
      )}

      <div
        className={cn(
          "overflow-y-auto",
          placement === "above" ? "max-h-72" : "max-h-96",
          showFilters && "border-t border-zinc-100",
        )}
      >
        {isFetching && results.length === 0 && (
          <div className="px-4 py-3 text-sm text-zinc-400">Searching…</div>
        )}

        {!isFetching && results.length === 0 && (
          <div className="px-4 py-3 text-sm text-zinc-400">
            No matching results
          </div>
        )}

        {results.map((item, index) => (
          <button
            key={`${item._type}:${item.id}`}
            type="button"
            // Keep the editor's selection intact when clicking a result.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onHoverIndex(index)}
            onClick={() => onSelect(item)}
            className={cn(
              "flex w-full items-center gap-3 px-4 py-2.5 text-left",
              index === activeIndex ? "bg-zinc-50" : "hover:bg-zinc-50",
            )}
          >
            {item._type === "staff" ? (
              <span
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
                  colorFor(item.id || item.name),
                )}
              >
                {initials(item.name)}
              </span>
            ) : (
              <AccountAvatar className="size-9 shrink-0" />
            )}

            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-zinc-900">
                {item.name}
              </span>
              <span className="block truncate text-xs text-zinc-500">
                {item._type === "staff"
                  ? [item.branch, item.email].filter(Boolean).join(" · ")
                  : [item.accountCode, item.email].filter(Boolean).join(" · ")}
              </span>
            </span>

            {item._type === "staff" && item.role && (
              <span className="ml-auto shrink-0 text-sm text-zinc-400">
                {item.role}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
